/**
 * 方向板块里的三台仪器。
 *
 * 首屏已经用骨架 / 节点图 / 权益曲线交代了三个领域的「形状」，这里刻意换成
 * 另外三台设备：同一个领域，第二个视角。一台机架上不会并排放三台一模一样的表。
 *
 *   CH.01  测角仪走纸   —— 关节角度随时间的曲线，带动作分期
 *   CH.02  执行时序     —— 智能体一次运行的泳道 trace，含重试
 *   CH.03  权益 + 水下  —— 净值曲线与回撤深度图
 *
 * 三个函数都是纯绘制：给 ctx / 尺寸 / 时间，画一帧。不持有状态，方便被
 * 同一个 <Instrument> 壳复用，也方便在 prefers-reduced-motion 下只画一帧。
 */
import { BONE, AMBER, EMBER, rgba, type RGB } from './rigs';

const MONO = '"SF Mono",ui-monospace,Menlo,monospace';
const HEI = '"PingFang SC","Hiragino Sans GB",system-ui,sans-serif';

export type InstrumentKind = 'goniometer' | 'trace' | 'equity';

/** 固定种子 LCG：形状每次加载都一样。随机变化的图表看起来是装饰，不是数据 */
function seeded(seed: number) {
  let s = seed;
  return () => ((s = (s * 16807) % 2147483647) / 2147483647);
}

function frame(ctx: CanvasRenderingContext2D, W: number, H: number) {
  ctx.clearRect(0, 0, W, H);
  ctx.textBaseline = 'alphabetic';
  ctx.lineCap = 'butt';
}

function gridlines(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, rows: number) {
  ctx.strokeStyle = rgba(BONE, 0.055);
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let i = 0; i <= rows; i++) {
    const y = y0 + ((y1 - y0) * i) / rows;
    ctx.moveTo(x0, y);
    ctx.lineTo(x1, y);
  }
  ctx.stroke();
}

function tag(ctx: CanvasRenderingContext2D, x: number, y: number, s: string, col: RGB, a = 0.55, sz = 9) {
  ctx.font = `${sz}px ${MONO}`;
  ctx.fillStyle = rgba(col, a);
  ctx.fillText(s, x, y);
}

/* ══════════════════════════════════════════════════════════════
   CH.01 · 测角仪走纸
   左侧是标尺化的角度轴，曲线从右侧推进，像老式记录仪出纸。
   底部的分期条把连续动作切成「支撑 / 摆动」，这是把姿态变成
   可判定指标的第一步——也是这个方向真正的技术内容。
   ══════════════════════════════════════════════════════════════ */
function drawGoniometer(ctx: CanvasRenderingContext2D, W: number, H: number, t: number) {
  frame(ctx, W, H);
  const tight = W < 520;
  const padL = tight ? 38 : 46, padR = tight ? 74 : 92, padT = 26, padB = 46;
  const x0 = padL, x1 = W - padR, y0 = padT, y1 = H - padB;
  const span = 6.2;                       // 横轴 6.2 秒
  const angle = (phase: number, k: number) =>
    // 两条腿的角度都是「基线 + 主谐波 + 小二次谐波」，读起来像真实关节而非正弦
    k === 0
      ? 104 + 46 * Math.sin(phase) + 9 * Math.sin(phase * 2 + 0.7)
      : 148 - 44 * Math.max(0, Math.sin(phase + 1.1)) - 7 * Math.sin(phase * 2);

  gridlines(ctx, x0, y0, x1, y1, 4);

  /* 纵轴：角度刻度 */
  ctx.strokeStyle = rgba(BONE, 0.16);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x0, y0); ctx.lineTo(x0, y1); ctx.lineTo(x1, y1);
  ctx.stroke();
  ctx.textAlign = 'right';
  for (let i = 0; i <= 4; i++) {
    const v = 180 - i * 45;
    tag(ctx, x0 - 8, y0 + ((y1 - y0) * i) / 4 + 3, `${v}°`, BONE, 0.34, 8.5);
  }
  ctx.textAlign = 'left';

  /* 动作分期条：支撑期底纹 */
  const cyc = 1.55;
  for (let s = -1; s < span / cyc + 1; s++) {
    const ts = t - span + s * cyc;
    const a = x0 + ((ts - (t - span)) / span) * (x1 - x0);
    const b = a + (cyc * 0.42 * (x1 - x0)) / span;
    const aa = Math.max(x0, a), bb = Math.min(x1, b);
    if (bb <= aa) continue;
    ctx.fillStyle = rgba(BONE, 0.028);
    ctx.fillRect(aa, y0, bb - aa, y1 - y0);
  }

  /* 两条曲线 */
  const traces: { k: number; col: RGB; name: string }[] = [
    { k: 0, col: AMBER, name: 'R_KNEE' },
    { k: 1, col: BONE, name: 'R_ELBOW' },
  ];
  const STEP = 2;
  traces.forEach(({ k, col }) => {
    ctx.strokeStyle = rgba(col, 0.9);
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    for (let px = x0; px <= x1; px += STEP) {
      const ts = t - span + ((px - x0) / (x1 - x0)) * span;
      const v = angle(ts * 2.0, k);
      const py = y1 - ((v / 180) * (y1 - y0));
      px === x0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
    }
    ctx.stroke();
  });

  /* 右侧当前读数 + 走纸头 */
  traces.forEach(({ k, col, name }, i) => {
    const v = angle(t * 2.0, k);
    const py = y1 - ((v / 180) * (y1 - y0));
    ctx.strokeStyle = rgba(col, 0.28);
    ctx.setLineDash([2, 3]);
    ctx.beginPath(); ctx.moveTo(x1, py); ctx.lineTo(W - 12, py); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = rgba(col, 1);
    ctx.beginPath(); ctx.arc(x1, py, 2.6, 0, 6.284); ctx.fill();
    const rowY = y0 + 12 + i * 34;
    tag(ctx, x1 + 9, rowY, name, BONE, 0.34, tight ? 7.5 : 8.5);
    ctx.font = `${tight ? 11 : 13}px ${MONO}`;
    ctx.fillStyle = rgba(col, 0.95);
    ctx.fillText(`${v.toFixed(1)}°`, x1 + 9, rowY + 15);
  });

  /* 底部：时间轴 + 分期图例 */
  ctx.textAlign = 'left';
  tag(ctx, x0, y1 + 16, '−6.2s', BONE, 0.3, 8.5);
  ctx.textAlign = 'right';
  tag(ctx, x1, y1 + 16, 'NOW', AMBER, 0.5, 8.5);
  ctx.textAlign = 'left';
  ctx.fillStyle = rgba(BONE, 0.028);
  ctx.fillRect(x0, y1 + 26, 16, 9);
  ctx.font = `10px ${HEI}`;
  ctx.fillStyle = rgba(BONE, 0.36);
  ctx.fillText('支撑期', x0 + 22, y1 + 34);
  ctx.fillText('摆动期', x0 + 92, y1 + 34);
  ctx.strokeStyle = rgba(BONE, 0.14);
  ctx.strokeRect(x0 + 70, y1 + 26, 16, 9);
}

/* ══════════════════════════════════════════════════════════════
   CH.02 · 执行时序
   一次智能体运行的 trace：五条泳道，span 按时间落位，
   ember 的短块是重试。右侧累计 token 与耗时。
   ══════════════════════════════════════════════════════════════ */
type Span = { lane: number; t0: number; dur: number; label: string; retry?: boolean };

const LANES = ['规划', '检索', '写码', '执行', '评估'];
const SPANS: Span[] = (() => {
  const r = seeded(7717);
  const out: Span[] = [];
  let cursor = 0.15;
  const script: [number, string, number][] = [
    [0, '拆解目标', 0.6], [1, '查文档', 0.9], [2, '生成补丁', 1.5],
    [3, '跑测试', 1.1], [4, '判定失败', 0.4], [2, '改补丁', 1.0],
    [3, '重跑测试', 1.0], [4, '通过', 0.35], [1, '查接口', 0.7],
    [2, '写集成', 1.2], [3, '部署', 0.8], [4, '验收', 0.5],
  ];
  script.forEach(([lane, label, dur], i) => {
    out.push({ lane, t0: cursor, dur, label, retry: label.startsWith('重') || label.startsWith('改') });
    cursor += dur * (0.55 + r() * 0.25);
    if (i % 4 === 3) cursor += 0.25;
  });
  return out;
})();
/** 轴长由数据算出，不写死——写死会在右侧留出一段永远空着的死区 */
const TOTAL = Math.max(...SPANS.map((s) => s.t0 + s.dur)) + 0.3;

function drawTrace(ctx: CanvasRenderingContext2D, W: number, H: number, t: number) {
  frame(ctx, W, H);
  const tight = W < 520;
  const padL = tight ? 42 : 54, padR = tight ? 84 : 116, padT = 22, padB = 34;
  const x0 = padL, x1 = W - padR, y0 = padT, y1 = H - padB;
  const laneH = (y1 - y0) / LANES.length;
  const playhead = (t * 0.85) % (TOTAL + 2.2);     // 跑完停 2.2 秒再重来
  const X = (ts: number) => x0 + (ts / TOTAL) * (x1 - x0);

  /* 泳道 */
  LANES.forEach((name, i) => {
    const y = y0 + i * laneH;
    ctx.strokeStyle = rgba(BONE, 0.05);
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(x0, y); ctx.lineTo(x1, y); ctx.stroke();
    ctx.font = `10px ${HEI}`;
    ctx.textAlign = 'right';
    ctx.fillStyle = rgba(BONE, 0.4);
    ctx.fillText(name, x0 - 10, y + laneH / 2 + 4);
    ctx.textAlign = 'left';
  });
  ctx.strokeStyle = rgba(BONE, 0.16);
  ctx.beginPath(); ctx.moveTo(x0, y1); ctx.lineTo(x1, y1); ctx.stroke();

  /* span —— 整条 trace 常驻：这是一次「已经跑完」的运行，播放头只是在回放它。
     从空图长出来的版本一个循环里大半时间是空的，既不好看也不像 trace viewer。 */
  let done = 0, tokens = 0, retries = 0;
  SPANS.forEach((s) => {
    const y = y0 + s.lane * laneH + laneH * 0.24;
    const h = laneH * 0.52;
    const col = s.retry ? EMBER : AMBER;
    const xa = X(s.t0), xb = X(s.t0 + s.dur);
    const passed = Math.max(0, Math.min(s.dur, playhead - s.t0));

    // 未扫到：只留轮廓，像还没点亮的刻度
    ctx.strokeStyle = rgba(col, 0.2);
    ctx.lineWidth = 1;
    ctx.strokeRect(xa + 0.5, y + 0.5, Math.max(1, xb - xa - 1), h - 1);

    if (passed > 0) {
      const xp = X(s.t0 + passed);
      ctx.fillStyle = rgba(col, 0.16);
      ctx.fillRect(xa, y, xp - xa, h);
      ctx.strokeStyle = rgba(col, 0.75);
      ctx.strokeRect(xa + 0.5, y + 0.5, Math.max(1, xp - xa - 1), h - 1);
    }
    if (passed >= s.dur) {
      done++;
      tokens += Math.round(s.dur * 1180);
      if (s.retry) retries++;
    }
    if (xb - xa > 46) {
      ctx.font = `9px ${HEI}`;
      ctx.fillStyle = rgba(BONE, passed >= s.dur ? 0.62 : 0.2);
      ctx.fillText(s.label, xa + 6, y + h / 2 + 3.5);
    }
  });

  /* 播放头 */
  if (playhead < TOTAL) {
    const px = X(playhead);
    ctx.strokeStyle = rgba(BONE, 0.34);
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(px, y0 - 6); ctx.lineTo(px, y1 + 4); ctx.stroke();
    ctx.fillStyle = rgba(BONE, 0.8);
    ctx.beginPath();
    ctx.moveTo(px - 4, y0 - 10); ctx.lineTo(px + 4, y0 - 10); ctx.lineTo(px, y0 - 4);
    ctx.closePath(); ctx.fill();
  }

  /* 右侧累计读数 */
  const rx = x1 + 14;
  const stats: [string, string, RGB][] = [
    ['STEP', `${done}/${SPANS.length}`, BONE],
    ['TOKENS', tokens > 999 ? `${(tokens / 1000).toFixed(1)}k` : `${tokens}`, AMBER],
    ['RETRY', `${retries}`, retries ? EMBER : BONE],
    ['ELAPSED', `${Math.min(playhead, TOTAL).toFixed(1)}s`, BONE],
  ];
  stats.forEach(([k, v, col], i) => {
    const y = y0 + 14 + i * 26;
    tag(ctx, rx, y, k, BONE, 0.3, 8.5);
    ctx.font = `12px ${MONO}`;
    ctx.fillStyle = rgba(col, 0.92);
    ctx.fillText(v, rx, y + 14);
  });

  ctx.textAlign = 'left';
  tag(ctx, x0, y1 + 16, '0s', BONE, 0.3, 8.5);
  ctx.textAlign = 'right';
  tag(ctx, x1, y1 + 16, `${TOTAL.toFixed(1)}s`, BONE, 0.3, 8.5);
  ctx.textAlign = 'left';
}

/* ══════════════════════════════════════════════════════════════
   CH.03 · 权益 + 水下回撤
   上半是净值，下半是「水下图」——距历史高点的回撤深度。
   水下图是风控的语言：策略好不好看上半，能不能拿得住看下半。
   ══════════════════════════════════════════════════════════════ */
const SERIES: number[] = (() => {
  const r = seeded(4211);
  const out: number[] = [];
  let v = 0;
  // 参数调到「可信」而不是「好看」：+16% 收益配 −8.8% 最大回撤。
  // 原来的 +17%/−2.9% 在懂行的人眼里是好到不真实，反而毁掉这台仪器的说服力。
  for (let i = 0; i < 168; i++) {
    v += (r() - 0.45) * 0.04;
    out.push(v);
  }
  return out;
})();
const UNDERWATER: number[] = (() => {
  let peak = -9;
  return SERIES.map((v) => { peak = Math.max(peak, v); return v - peak; });
})();
const MAXDD = Math.min(...UNDERWATER);
const MAXDD_I = UNDERWATER.indexOf(MAXDD);

function drawEquity(ctx: CanvasRenderingContext2D, W: number, H: number, t: number) {
  frame(ctx, W, H);
  const tight = W < 520;
  const padL = tight ? 38 : 44, padR = tight ? 76 : 96, padT = 22, padB = 34;
  const x0 = padL, x1 = W - padR;
  const yTop = padT, ySplit = padT + (H - padT - padB) * 0.62, yBot = H - padB;
  const grow = Math.min(1, (t % 20) / 4.0);       // 4 秒画完，之后停在完整曲线上 16 秒
  const nn = Math.max(2, Math.round(SERIES.length * grow));
  const hi = Math.max(...SERIES), lo = Math.min(...SERIES);
  const X = (i: number) => x0 + ((x1 - x0) * i) / (SERIES.length - 1);
  const Y = (v: number) => ySplit - 8 - ((v - lo) / (hi - lo || 1)) * (ySplit - 8 - yTop);

  gridlines(ctx, x0, yTop, x1, ySplit - 8, 3);

  /* 净值面积 + 线 */
  ctx.beginPath();
  ctx.moveTo(X(0), Y(SERIES[0]));
  for (let i = 1; i < nn; i++) ctx.lineTo(X(i), Y(SERIES[i]));
  const lastX = X(nn - 1);
  ctx.lineTo(lastX, ySplit - 8); ctx.lineTo(X(0), ySplit - 8); ctx.closePath();
  ctx.fillStyle = rgba(EMBER, 0.06);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(X(0), Y(SERIES[0]));
  for (let i = 1; i < nn; i++) ctx.lineTo(X(i), Y(SERIES[i]));
  ctx.strokeStyle = rgba(EMBER, 0.92);
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.fillStyle = rgba(EMBER, 1);
  ctx.beginPath(); ctx.arc(lastX, Y(SERIES[nn - 1]), 2.8, 0, 6.284); ctx.fill();

  ctx.textAlign = 'right';
  for (let i = 0; i <= 3; i++) {
    const v = lo + ((hi - lo) * i) / 3;
    tag(ctx, x0 - 8, Y(v) + 3, `${(v * 100).toFixed(0)}%`, BONE, 0.3, 8.5);
  }
  ctx.textAlign = 'left';
  tag(ctx, x0, yTop - 8, '净值', BONE, 0.34, 9);

  /* 水下图 */
  const uw = (v: number) => ySplit + 14 + (v / (MAXDD || -1)) * (yBot - ySplit - 14);
  ctx.strokeStyle = rgba(BONE, 0.12);
  ctx.beginPath(); ctx.moveTo(x0, ySplit + 14); ctx.lineTo(x1, ySplit + 14); ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(X(0), ySplit + 14);
  for (let i = 0; i < nn; i++) ctx.lineTo(X(i), uw(UNDERWATER[i]));
  ctx.lineTo(lastX, ySplit + 14);
  ctx.closePath();
  ctx.fillStyle = rgba(EMBER, 0.17);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(X(0), uw(UNDERWATER[0]));
  for (let i = 1; i < nn; i++) ctx.lineTo(X(i), uw(UNDERWATER[i]));
  ctx.strokeStyle = rgba(EMBER, 0.6);
  ctx.lineWidth = 1;
  ctx.stroke();
  tag(ctx, x0, ySplit + 8, '水下回撤', BONE, 0.34, 9);

  /* 最大回撤标注 —— 只在画到那一点之后出现 */
  if (nn > MAXDD_I) {
    const mx = X(MAXDD_I), my = uw(MAXDD);
    ctx.strokeStyle = rgba(EMBER, 0.5);
    ctx.setLineDash([2, 3]);
    ctx.beginPath(); ctx.moveTo(mx, ySplit + 14); ctx.lineTo(mx, my); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = rgba(EMBER, 0.95);
    ctx.beginPath(); ctx.arc(mx, my, 2.4, 0, 6.284); ctx.fill();
    ctx.font = `9px ${MONO}`;
    ctx.textAlign = mx > (x0 + x1) / 2 ? 'right' : 'left';
    ctx.fillText(`MDD ${(MAXDD * 100).toFixed(1)}%`, mx + (mx > (x0 + x1) / 2 ? -8 : 8), my + 3);
    ctx.textAlign = 'left';
  }

  /* 右侧指标 */
  const rx = x1 + 14;
  const cur = SERIES[nn - 1];
  const stats: [string, string, RGB][] = [
    ['RETURN', `${(cur * 100).toFixed(1)}%`, EMBER],
    ['MDD', `${(MAXDD * 100).toFixed(1)}%`, BONE],
    ['BARS', `${nn}`, BONE],
    ['MODE', 'BACKTEST', AMBER],
  ];
  stats.forEach(([k, v, col], i) => {
    const y = yTop + 14 + i * 26;
    tag(ctx, rx, y, k, BONE, 0.3, 8.5);
    ctx.font = `12px ${MONO}`;
    ctx.fillStyle = rgba(col, 0.92);
    ctx.fillText(v, rx, y + 14);
  });
}

export function drawInstrument(
  kind: InstrumentKind,
  ctx: CanvasRenderingContext2D,
  W: number,
  H: number,
  t: number,
) {
  if (kind === 'goniometer') drawGoniometer(ctx, W, H, t);
  else if (kind === 'trace') drawTrace(ctx, W, H, t);
  else drawEquity(ctx, W, H, t);
}
